/* Editorial Tenant Contact — hairline-underline correspondence form on
   the studio's canvas, paired with an editorial studio panel. Fully
   theme-reactive. Replaces the prior re-export of the founder cream
   Contact which clashed with the Editorial dark Layout.

   Preserves the existing POST /api/contact handler with the same
   { name, email, phone, message } payload shape. */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function EditorialContact() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = `${studioName} — Contact`;
  }, [studioName]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email, and a short message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Letter sent. We will reply within two business days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — please try again.");
    } finally {
      setSending(false);
    }
  }

  const fields = [
    { name: "name",  label: "Your name", type: "text",  placeholder: "Jane Doe" },
    { name: "email", label: "Email",     type: "email", placeholder: "jane@studio.com" },
    { name: "phone", label: "Telephone (optional)", type: "tel", placeholder: "+1 555 0100" },
  ];

  return (
    <div
      data-testid="editorial-tenant-contact"
      className="min-h-screen pt-28 pb-24"
      style={{ background: "var(--tenant-bg, #0A0908)", color: "var(--tenant-fg, #F5F2EC)" }}
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        {/* Chapter masthead */}
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
            Chapter IV · Correspondence
          </div>
          <div
            className="mt-5 h-px"
            style={{ width: "2rem", background: "var(--tenant-accent)" }}
            aria-hidden
          />
          <h1
            className="mt-10 leading-[0.95] tracking-tight"
            style={{
              fontFamily: "var(--tenant-serif)",
              fontWeight: 300,
              fontSize: "clamp(2.75rem, 7vw, 5.5rem)",
            }}
          >
            Write to us,{" "}
            <em style={{ fontStyle: "italic", color: "var(--tenant-accent)" }}>kindly</em>.
          </h1>
          <p
            className="mt-6 text-lg italic max-w-xl"
            style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-fg-soft, rgba(245,242,236,0.7))" }}
          >
            We answer every letter within two business days.
          </p>
        </div>

        {/* Two-column: form left, studio panel right */}
        <div className="mt-16 lg:mt-20 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20">
          {/* LEFT — hairline underline form */}
          <form
            onSubmit={onSubmit}
            className="space-y-10"
            data-testid="editorial-contact-form"
          >
            {fields.map((f) => (
              <label key={f.name} className="block">
                <span
                  className="block text-[10px] uppercase tracking-[0.38em] mb-3"
                  style={{ color: "var(--tenant-accent)" }}
                >
                  {f.label}
                </span>
                <input
                  type={f.type}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  placeholder={f.placeholder}
                  className="w-full bg-transparent border-0 border-b py-3 outline-none text-lg transition-colors"
                  style={{
                    borderBottomColor: "var(--tenant-fg-line, rgba(245,242,236,0.25))",
                    color: "var(--tenant-fg, #F5F2EC)",
                  }}
                  data-testid={`editorial-contact-${f.name}`}
                />
              </label>
            ))}
            <label className="block">
              <span
                className="block text-[10px] uppercase tracking-[0.38em] mb-3"
                style={{ color: "var(--tenant-accent)" }}
              >
                Message
              </span>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                placeholder="Tell us about the moment you'd like remembered."
                className="w-full bg-transparent border-0 border-b py-3 outline-none text-lg transition-colors resize-none"
                style={{
                  borderBottomColor: "var(--tenant-fg-line, rgba(245,242,236,0.25))",
                  color: "var(--tenant-fg, #F5F2EC)",
                }}
                data-testid="editorial-contact-message"
              />
            </label>
            <div className="pt-2">
              <button
                type="submit"
                disabled={sending}
                data-testid="editorial-contact-submit"
                className="inline-flex items-center gap-3 px-8 h-12 text-[10px] uppercase tracking-[0.38em] transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: "var(--tenant-fg, #F5F2EC)",
                  color: "var(--tenant-bg, #0A0908)",
                }}
              >
                {sending ? "Posting…" : "Post your letter →"}
              </button>
            </div>
            {sent ? (
              <p
                className="text-base italic"
                style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}
                data-testid="editorial-contact-success"
              >
                — Letter received. We&apos;ll reply within two business days.
              </p>
            ) : null}
          </form>

          {/* RIGHT — editorial studio panel */}
          <aside>
            <div
              className="p-8 lg:p-10 border"
              style={{
                borderColor: "var(--tenant-fg-line, rgba(245,242,236,0.15))",
                background: "var(--tenant-muted, #15120F)",
              }}
            >
              <div className="text-[10px] uppercase tracking-[0.38em]" style={{ color: "var(--tenant-accent)" }}>
                The Studio
              </div>
              <div
                className="mt-4 h-px"
                style={{ width: "2rem", background: "var(--tenant-accent)" }}
                aria-hidden
              />
              <p
                className="mt-6 text-2xl italic leading-tight"
                style={{ fontFamily: "var(--tenant-serif)" }}
              >
                {studioName}
                {settings?.contact_location ? (
                  <>
                    <br />
                    {settings.contact_location}
                  </>
                ) : null}
              </p>

              {settings?.contact_email ? (
                <div className="mt-8">
                  <div className="text-[10px] uppercase tracking-[0.38em]" style={{ color: "var(--tenant-accent)" }}>
                    Email
                  </div>
                  <a
                    href={`mailto:${settings.contact_email}`}
                    className="mt-2 inline-block text-lg italic hover:opacity-70 transition-opacity"
                    style={{ fontFamily: "var(--tenant-serif)" }}
                    data-testid="editorial-contact-email-link"
                  >
                    {settings.contact_email}
                  </a>
                </div>
              ) : null}

              <div className="mt-8">
                <div className="text-[10px] uppercase tracking-[0.38em]" style={{ color: "var(--tenant-accent)" }}>
                  Office hours
                </div>
                <p
                  className="mt-2 text-lg italic"
                  style={{ fontFamily: "var(--tenant-serif)" }}
                >
                  Tue — Sat / 10:00 — 18:00
                </p>
              </div>
            </div>
          </aside>
        </div>

        {/* Hairline divider with eyebrow */}
        <div className="mt-20 flex items-center gap-5">
          <div className="flex-1 h-px" style={{ background: "var(--tenant-fg-line, rgba(245,242,236,0.15))" }} />
          <span className="text-[10px] uppercase tracking-[0.38em] whitespace-nowrap" style={{ color: "var(--tenant-accent)" }}>
            By appointment
          </span>
          <div className="flex-1 h-px" style={{ background: "var(--tenant-fg-line, rgba(245,242,236,0.15))" }} />
        </div>
      </div>
    </div>
  );
}
